import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';
import { useCelebrationStore } from '../stores/celebrationStore';
import { audioEngine } from '../lib/audio/AudioEngine';
import { calculateIntensity, isInDangerZone } from '../lib/audio/utils/audioUtils';
import type { GamePhase } from '@beat-games/shared-types';

export function useGameAudio() {
  const {
    phase,
    countdownSeconds,
    elapsed,
    circle,
    cursorPosition,
    isInsideCircle,
    endReason,
    scoreRank,
  } = useGameStore();

  // Initialize refs with current values to prevent false triggers on mount
  const prevPhaseRef = useRef<GamePhase>(phase);
  const prevCountdownRef = useRef<number | null>(countdownSeconds);
  const prevInDangerRef = useRef<boolean>(false);
  const prevRankRef = useRef<number | null>(scoreRank);
  const isAmbientPlayingRef = useRef(false);
  const celebrationPlayedRef = useRef(phase === 'ended'); // If already ended on mount, treat as played

  const { checkAndTriggerCelebration } = useCelebrationStore();

  // Initialize audio context (must be called from user interaction)
  const initializeAudio = useCallback(async () => {
    await audioEngine.initialize();
  }, []);

  // Handle phase transitions
  useEffect(() => {
    const prevPhase = prevPhaseRef.current;

    // Countdown started
    if (phase === 'countdown' && prevPhase !== 'countdown') {
      // Audio engine should already be initialized
    }

    // Countdown tick
    if (phase === 'countdown' && countdownSeconds !== null) {
      if (countdownSeconds !== prevCountdownRef.current && countdownSeconds > 0) {
        audioEngine.playCountdownBeep(countdownSeconds);
      }
      prevCountdownRef.current = countdownSeconds;
    }

    // Game started (transition from countdown to playing)
    if (phase === 'playing' && prevPhase === 'countdown') {
      audioEngine.playGoSound();
      audioEngine.startAmbient();
      isAmbientPlayingRef.current = true;
    }

    // Game ended
    if (phase === 'ended' && prevPhase === 'playing') {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
      isAmbientPlayingRef.current = false;
      celebrationPlayedRef.current = false; // Reset for potential celebration
      prevInDangerRef.current = false; // Reset danger state

      // Play appropriate end sound
      if (endReason === 'completed') {
        audioEngine.playSuccessSound();
      } else {
        audioEngine.playFailureSound();
      }
    }

    // Reset (back to waiting)
    if (phase === 'waiting' && prevPhase !== 'waiting') {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
      isAmbientPlayingRef.current = false;
      celebrationPlayedRef.current = false;
      prevRankRef.current = null;
      prevInDangerRef.current = false; // Reset danger state
    }

    prevPhaseRef.current = phase;
  }, [phase, countdownSeconds, endReason]);

  // Handle celebration sounds when rank comes back from server
  useEffect(() => {
    // Only check when game has ended
    if (phase !== 'ended') return;

    // Only trigger once per game, and only when rank is newly set
    if (celebrationPlayedRef.current) return;
    if (scoreRank === prevRankRef.current) return;

    prevRankRef.current = scoreRank;

    // Check for podium celebration (rank 1, 2, or 3)
    const celebration = checkAndTriggerCelebration(scoreRank);

    if (celebration !== 'none') {
      celebrationPlayedRef.current = true;

      // Delay celebration sound slightly so it doesn't overlap with end sound
      setTimeout(() => {
        if (celebration === 'rank1') {
          audioEngine.playHighScoreSound();
        } else if (celebration === 'rank2') {
          audioEngine.playPersonalBestSound();
        } else if (celebration === 'rank3') {
          audioEngine.playRank3Sound();
        }
      }, 800);
    }
  }, [phase, scoreRank, checkAndTriggerCelebration]);

  // Update ambient intensity based on game progress
  useEffect(() => {
    if (phase === 'playing' && isAmbientPlayingRef.current) {
      const intensity = calculateIntensity(elapsed, circle);
      audioEngine.setAmbientIntensity(intensity);
    }
  }, [phase, elapsed, circle]);

  // Handle danger zone and outside circle warning
  useEffect(() => {
    // Stop alert and reset if not in playing phase
    if (phase !== 'playing') {
      // Always stop alert when not playing, regardless of prevInDangerRef state
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
      return;
    }

    if (!circle || !cursorPosition) {
      // Stop alert if data is unavailable during playing phase
      audioEngine.stopAlert();
      prevInDangerRef.current = false;
      return;
    }

    // Check if in danger zone (outer 20% of circle) or outside
    const inDangerZone = isInDangerZone(cursorPosition.x, cursorPosition.y, circle);
    const inDanger = inDangerZone || !isInsideCircle;

    const wasInDanger = prevInDangerRef.current;

    if (inDanger && !wasInDanger) {
      // Just entered danger zone or went outside
      audioEngine.startAlert();
    } else if (!inDanger && wasInDanger) {
      // Back to safe zone
      audioEngine.stopAlert();
    }

    prevInDangerRef.current = inDanger;
  }, [phase, circle, cursorPosition, isInsideCircle]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioEngine.stopAmbient();
      audioEngine.stopAlert();
    };
  }, []);

  return {
    initializeAudio,
    isInitialized: audioEngine.isInitialized(),
  };
}
